// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — the pure nonogram core: the immutable puzzle (solution + clues), the LINE SOLVER,
// and the deterministic generator. A board is a pure function of (mode, seed) — the whole
// P2P-sync surface — and it is generated SOLVABLE-BY-LOGIC and UNIQUE by construction:
//
//   1. draw a random bitmap at the mode's density,
//   2. compute its row/column clues,
//   3. run the LINE SOLVER — repeatedly, for each row and column, force any cell that is
//      filled (or empty) in EVERY placement of that line's clue consistent with what is
//      already known — to a fixpoint.
//
// A puzzle the line solver fully determines is **line-solvable**, which means it can be
// solved by pure deduction with NO guessing, and — crucially — that its solution is UNIQUE
// (two solutions would leave the disagreeing cells undetermined). Accept the first candidate
// that is line-solvable; otherwise re-draw from a sub-seed, up to a budget. A guaranteed
// line-solvable fallback (horizontal stripes) makes generation TOTAL so a daily seed can
// never crash or hang — the sim asserts the fallback is never reached across a large sweep.

import { hashSeed, makeRng } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';

export interface Board {
  readonly size: number;
  /** row-major, 1 = filled, 0 = empty. */
  readonly solution: Uint8Array;
  readonly rowClues: number[][];
  readonly colClues: number[][];
  readonly filledCount: number;
  /** the per-board seed used (mode+seed+index folded in) — for debugging/parity. */
  readonly boardSeed: number;
  /** true only if the guaranteed stripes fallback was used (should never happen in play). */
  readonly fallback: boolean;
}

/** Cell knowledge for the solver: -1 unknown, 0 empty, 1 filled. */
export type Known = Int8Array;

/** Run-length encode a line of 0/1 into block lengths. An all-empty line → []. */
export function lineClue(get: (i: number) => number, len: number): number[] {
  const out: number[] = [];
  let run = 0;
  for (let i = 0; i < len; i++) {
    if (get(i)) run++;
    else if (run > 0) {
      out.push(run);
      run = 0;
    }
  }
  if (run > 0) out.push(run);
  return out;
}

/** All row and column clues for a solution bitmap. */
export function cluesFromSolution(solution: Uint8Array, size: number): { rowClues: number[][]; colClues: number[][] } {
  const rowClues: number[][] = [];
  const colClues: number[][] = [];
  for (let r = 0; r < size; r++) rowClues.push(lineClue((c) => solution[r * size + c], size));
  for (let c = 0; c < size; c++) colClues.push(lineClue((r) => solution[r * size + c], size));
  return { rowClues, colClues };
}

/**
 * Tighten one line: for the given clue and current knowledge, return a line whose cells are
 * forced filled (1) / empty (0) / still unknown (-1), by intersecting ALL placements of the
 * clue consistent with `known`. Returns null if there is NO consistent placement.
 */
export function solveLine(clue: number[], known: Known): Known | null {
  const L = known.length;
  const canFill = new Uint8Array(L);
  const canEmpty = new Uint8Array(L);
  const line = new Int8Array(L);
  let any = false;

  // suffix minimal span (block lengths + gaps) for blocks [k..end]
  const suffix = new Int32Array(clue.length + 1);
  for (let k = clue.length - 1; k >= 0; k--) {
    suffix[k] = clue[k] + (k + 1 < clue.length ? 1 + suffix[k + 1] : 0);
  }

  const record = (): void => {
    any = true;
    for (let i = 0; i < L; i++) {
      if (line[i] === 1) canFill[i] = 1;
      else canEmpty[i] = 1;
    }
  };

  const place = (blockIdx: number, pos: number): void => {
    if (blockIdx === clue.length) {
      for (let i = pos; i < L; i++) {
        if (known[i] === 1) return; // a known-filled cell would be left empty → invalid
        line[i] = 0;
      }
      record();
      return;
    }
    const len = clue[blockIdx];
    const maxStart = L - suffix[blockIdx];
    for (let start = pos; start <= maxStart; start++) {
      // cells [pos, start) are empty; a known-filled one there kills this and every larger start
      if (start > pos && known[start - 1] === 1) break;
      // cells [start, start+len) are filled; a known-empty one there kills this start (try larger)
      let badFill = false;
      for (let i = start; i < start + len; i++) {
        if (known[i] === 0) {
          badFill = true;
          break;
        }
      }
      if (badFill) continue;
      for (let i = pos; i < start; i++) line[i] = 0;
      for (let i = start; i < start + len; i++) line[i] = 1;
      const gap = start + len;
      if (blockIdx < clue.length - 1) {
        if (gap < L && known[gap] === 1) continue; // the mandatory gap can't be a filled cell
        if (gap < L) line[gap] = 0;
        place(blockIdx + 1, gap + 1);
      } else {
        place(blockIdx + 1, gap);
      }
    }
  };

  place(0, 0);
  if (!any) return null;
  const out = new Int8Array(L);
  for (let i = 0; i < L; i++) {
    if (canFill[i] && !canEmpty[i]) out[i] = 1;
    else if (canEmpty[i] && !canFill[i]) out[i] = 0;
    else out[i] = -1;
  }
  return out;
}

export interface LineSolveResult {
  grid: Known; // -1/0/1, row-major
  solved: boolean; // fully determined
  contradiction: boolean; // clues admit no solution (never for real boards)
}

/** Run line-solver propagation to a fixpoint over all rows and columns. */
export function lineSolve(rowClues: number[][], colClues: number[][], size: number): LineSolveResult {
  const grid = new Int8Array(size * size).fill(-1);
  const rowBuf = new Int8Array(size);
  let changed = true;
  let guard = 0;
  while (changed) {
    changed = false;
    if (++guard > size * size + 4) break; // propagation cannot need more passes than cells
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) rowBuf[c] = grid[r * size + c];
      const res = solveLine(rowClues[r], rowBuf);
      if (res === null) return { grid, solved: false, contradiction: true };
      for (let c = 0; c < size; c++) {
        if (res[c] !== -1 && grid[r * size + c] === -1) {
          grid[r * size + c] = res[c];
          changed = true;
        }
      }
    }
    for (let c = 0; c < size; c++) {
      for (let r = 0; r < size; r++) rowBuf[r] = grid[r * size + c];
      const res = solveLine(colClues[c], rowBuf);
      if (res === null) return { grid, solved: false, contradiction: true };
      for (let r = 0; r < size; r++) {
        if (res[r] !== -1 && grid[r * size + c] === -1) {
          grid[r * size + c] = res[r];
          changed = true;
        }
      }
    }
  }
  let solved = true;
  for (let i = 0; i < grid.length; i++) {
    if (grid[i] === -1) {
      solved = false;
      break;
    }
  }
  return { grid, solved, contradiction: false };
}

const MAX_ATTEMPTS = 800;

function countFilled(sol: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < sol.length; i++) n += sol[i];
  return n;
}

function toBoard(size: number, solution: Uint8Array, boardSeed: number, fallback: boolean): Board {
  const { rowClues, colClues } = cluesFromSolution(solution, size);
  return { size, solution, rowClues, colClues, filledCount: countFilled(solution), boardSeed, fallback };
}

/** Horizontal-stripes board: some rows fully filled, the rest empty. ALWAYS line-solvable
 *  (each row clue is [size] or [], forcing that row outright) and unique. The total fallback. */
function stripesBoard(size: number, boardSeed: number): Board {
  const rng = makeRng(hashSeed(`${boardSeed}:stripes`));
  const full: boolean[] = [];
  for (let r = 0; r < size; r++) full.push(rng() < 0.5);
  if (full.every((f) => f) || full.every((f) => !f)) full[0] = !full[0]; // never all-same
  const solution = new Uint8Array(size * size);
  for (let r = 0; r < size; r++) if (full[r]) for (let c = 0; c < size; c++) solution[r * size + c] = 1;
  return toBoard(size, solution, boardSeed, true);
}

/**
 * A line-solvable, unique board at ~`density` fill. Deterministic in `boardSeed`. Draws
 * random candidates and accepts the first the line solver cracks; falls back to stripes only
 * if the (large) budget is exhausted — which the sim asserts never happens in normal play.
 */
export function makeBoard(size: number, density: number, boardSeed: number): Board {
  const cells = size * size;
  const minFill = Math.floor(cells * 0.3);
  const maxFill = Math.ceil(cells * 0.72);
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const rng = makeRng(hashSeed(`${boardSeed}:${attempt}`));
    const solution = new Uint8Array(cells);
    let filled = 0;
    for (let i = 0; i < cells; i++) {
      if (rng() < density) {
        solution[i] = 1;
        filled++;
      }
    }
    if (filled < minFill || filled > maxFill) continue;
    const { rowClues, colClues } = cluesFromSolution(solution, size);
    const res = lineSolve(rowClues, colClues, size);
    if (!res.solved) continue;
    // line-solvable ⇒ unique; the solved grid must equal the source bitmap
    let matches = true;
    for (let i = 0; i < cells; i++) {
      if (res.grid[i] !== solution[i]) {
        matches = false;
        break;
      }
    }
    if (matches) return toBoard(size, solution, boardSeed, false);
  }
  return stripesBoard(size, boardSeed);
}

/** Per-board seed for board `index` of a run (0 for single-board modes; 0,1,2… for Blitz). */
export function boardSeedFor(mode: Mode, seed: number, index: number): number {
  return hashSeed(`hatch:${mode.id}:${seed >>> 0}:${index}`);
}

/** The `index`-th board of a (mode, seed) run. */
export function boardFor(mode: Mode, seed: number, index: number): Board {
  return makeBoard(mode.size, mode.density, boardSeedFor(mode, seed, index));
}
